import { Link } from 'react-router-dom'

import type { MeasurementCoverage } from '@/api/types'
import { Card, EmptyState, formatInteger, formatPercent, FunnelBars, Icon, percentChange } from '@/design'
import { formatDateKey, toDateKey } from '@/lib/filters'

import { CardQueryBody } from './CardQueryBody'
import { ChangeText } from './ChangeText'
import { useDrillDown, useOverviewQuery } from './hooks'

/** What the funnel can see: the API's own sentence, when measuring began, and how much commerce it can tie to a session. */
function Coverage({ coverage }: { coverage: MeasurementCoverage }) {
  const share = coverage.ordersWithSessionShare
  return (
    <div className="ov-funnel__coverage" data-testid="funnel-coverage">
      <p className="ov-funnel__note">{coverage.note}</p>
      <dl className="ov-funnel__facts">
        {coverage.firstEventAt ? (
          <div>
            <dt>First measured event</dt>
            <dd>{formatDateKey(toDateKey(new Date(coverage.firstEventAt)), true)}</dd>
          </div>
        ) : null}
        <div>
          <dt>Orders with a measured session</dt>
          <dd>
            {share === null
              ? '—'
              : `${formatPercent(share, 0)} (${formatInteger(coverage.ordersWithSession)} of ${formatInteger(coverage.orders)})`}
          </dd>
        </div>
      </dl>
    </div>
  )
}

/** The seven-stage website funnel, each stage a link into funnel analytics at that stage. */
export function FunnelCard() {
  const funnel = useOverviewQuery('funnel', (client, filters) => client.funnel(filters))
  const href = useDrillDown()
  const stages = funnel.data?.data ?? []
  const first = stages[0]
  const last = stages[stages.length - 1]
  const measured = first?.measured !== false && last?.measured !== false
  const conversion = measured && first && last && first.count > 0 ? last.count / first.count : null
  const previousConversion =
    first?.previousCount && last?.previousCount !== null && last?.previousCount !== undefined
      ? last.previousCount / first.previousCount
      : null

  return (
    <Card
      className="ov-funnel"
      title="Website funnel"
      subtitle="From visitor to purchase"
      busy={funnel.isFetching}
      footer={
        <Link className="ov-footer-link" to={href({ path: '/analytics/funnel' })}>
          Open funnel analytics <Icon name="arrowRight" size={14} />
        </Link>
      }
    >
      <CardQueryBody query={funnel} loadingRows={7} errorDescription="The funnel could not be loaded.">
        {({ meta }) =>
          !first ? null : stages.every((stage) => stage.measured === false) ? (
            // No stage had an event in the period: nothing is measured yet, which is not a funnel of zeros.
            <EmptyState variant="no-data" compact title="No measured sessions in this period" description={meta.coverage?.note} />
          ) : first.count === 0 ? (
            <EmptyState variant="no-results" compact />
          ) : (
            <>
              {conversion !== null ? (
                <p className="ov-funnel__summary">
                  <span>Visitor to purchase</span>
                  <strong>{formatPercent(conversion)}</strong>
                  <ChangeText
                    change={previousConversion === null ? null : percentChange(conversion, previousConversion)}
                    label={meta.period?.compareLabel}
                  />
                </p>
              ) : null}
              <FunnelBars
                caption="Website funnel stages"
                stages={stages.map((stage) => ({
                  key: stage.key,
                  label: stage.label,
                  count: stage.count,
                  measured: stage.measured,
                  href: href(stage.drillDown),
                }))}
              />
              {meta.coverage ? <Coverage coverage={meta.coverage} /> : null}
            </>
          )
        }
      </CardQueryBody>
    </Card>
  )
}
